import { isAxiosError } from "axios";
import { parseCalendarDate } from "./dateFormatters";

export const AiRunMergePolicy = {
  AUTO_MERGE: "auto_merge",
  FORCE_OVERWRITE: "force_overwrite",
  PREVIEW_ONLY: "preview_only",
} as const;

export type AiRunMergePolicy = (typeof AiRunMergePolicy)[keyof typeof AiRunMergePolicy];

/**
 * Backend 409 code for a script breakdown start on a version that already has tags without
 * force_overwrite. It is refused before any credits are reserved.
 */
export const BREAKDOWN_ALREADY_TAGGED = "BREAKDOWN_ALREADY_TAGGED";

export function isBreakdownAlreadyTaggedError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const data: unknown = error.response?.data;
  return (
    error.response?.status === 409 &&
    typeof data === "object" &&
    data !== null &&
    (data as { code?: unknown }).code === BREAKDOWN_ALREADY_TAGGED
  );
}

export type AiMergeReportCounts = {
  ai_updated?: number;
  user_preserved?: number;
  pending_review?: number;
  added?: number;
};

export type AiMergeReportPayload = {
  policy?: string;
  feature_key?: string;
  manifest_version?: string;
  counts?: AiMergeReportCounts;
};

export function formatAiMergeReportToast(report: AiMergeReportPayload | null | undefined): string {
  if (!report?.counts) {
    return "AI run finished.";
  }
  const c = report.counts;
  const parts: string[] = [];
  if (typeof c.ai_updated === "number" && c.ai_updated > 0) {
    parts.push(`${c.ai_updated} updated`);
  }
  if (typeof c.user_preserved === "number" && c.user_preserved > 0) {
    parts.push(`${c.user_preserved} of your edits kept`);
  }
  if (typeof c.pending_review === "number" && c.pending_review > 0) {
    parts.push(`${c.pending_review} need review`);
  }
  if (typeof c.added === "number" && c.added > 0) {
    parts.push(`${c.added} added`);
  }
  if (!parts.length) {
    return "AI run finished.";
  }
  return `AI run finished: ${parts.join(", ")}.`;
}

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

/**
 * Scenes the schedule worker placed on a day an assigned actor is unavailable, from the
 * schedule feature status metadata (`merge_report.counts.availability_conflicts`; the list
 * `merge_report.availability_conflicts` is capped, so it is only a fallback). 0 when absent.
 */
export function getScheduleAvailabilityConflictCount(metadata: unknown): number {
  if (!metadata || typeof metadata !== "object") return 0;
  const report = (metadata as { merge_report?: unknown }).merge_report;
  if (!report || typeof report !== "object") return 0;
  const { counts, availability_conflicts: list } = report as {
    counts?: { availability_conflicts?: unknown } | null;
    availability_conflicts?: unknown;
  };
  const count = counts?.availability_conflicts;
  if (isNonNegativeInteger(count)) return count;
  return Array.isArray(list) ? list.length : 0;
}

export type ScheduleAvailabilityConflictExample = {
  /** Scene number as the stripboard shows it (`scene_sequence`). */
  scene: string;
  /** Shoot day, short form such as "Nov 2". */
  date: string;
};

const MAX_CONFLICT_EXAMPLES = 3;

/** Up to 3 valid entries from `merge_report.availability_conflicts`, in report order. */
export function getScheduleAvailabilityConflictExamples(
  metadata: unknown
): ScheduleAvailabilityConflictExample[] {
  if (!metadata || typeof metadata !== "object") return [];
  const report = (metadata as { merge_report?: unknown }).merge_report;
  if (!report || typeof report !== "object") return [];
  const list = (report as { availability_conflicts?: unknown }).availability_conflicts;
  if (!Array.isArray(list)) return [];

  const examples: ScheduleAvailabilityConflictExample[] = [];
  for (const entry of list) {
    if (examples.length >= MAX_CONFLICT_EXAMPLES) break;
    if (!entry || typeof entry !== "object") continue;
    const { scene_sequence: scene, shooting_date: date } = entry as {
      scene_sequence?: unknown;
      shooting_date?: unknown;
    };
    if ((typeof scene !== "number" && typeof scene !== "string") || String(scene) === "") continue;
    if (typeof date !== "string") continue;
    const day = parseCalendarDate(date);
    if (!day.isValid()) continue;
    examples.push({ scene: String(scene), date: day.format("MMM D") });
  }
  return examples;
}

const joinWithAnd = (items: string[]) =>
  items.length <= 1
    ? (items[0] ?? "")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

export function formatAvailabilityConflictSentence(
  count: number,
  examples: ScheduleAvailabilityConflictExample[] = []
): string {
  const base =
    count === 1
      ? "1 scene falls on a day an assigned actor is unavailable"
      : `${count} scenes fall on days an assigned actor is unavailable`;
  if (!examples.length) return `${base}. Check the stripboard.`;

  const named = joinWithAnd(examples.map((e) => `scene ${e.scene} (${e.date})`));
  const lead = count > examples.length ? `${base}, including ${named}.` : `${base}: ${named}.`;
  return `${lead} Check ${count === 1 ? "it" : "them"} on the stripboard.`;
}

export function formatScheduleAvailabilityConflictToast(
  count: number,
  examples: ScheduleAvailabilityConflictExample[] = []
): string {
  return `Schedule saved. ${formatAvailabilityConflictSentence(count, examples)}`;
}
